import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useProject } from '../../context/ProjectContext';
import projectService from '../../services/projectService';
import api from '../../api';
import {
  FaPlus,
  FaSearch,
  FaCog,
  FaList,
  FaTh,
  FaChartBar,
  FaEye,
} from 'react-icons/fa';
import checklistTasksGif from '../../assets/checklist-tasks.gif';
import pencilIcon from '../../assets/pencil.svg';
import dotsIcon from '../../assets/dots.svg';
import trashIcon from '../../assets/trash.svg';
import listIcon from '../../assets/list-tab.svg';
import boardIcon from '../../assets/board-view.svg';
import ganttIcon from '../../assets/gant-view.svg';
import statusCompleteIcon from '../../assets/status-complete.svg';
import statusHoldIcon from '../../assets/status-hold.svg';
import statusTodoIcon from '../../assets/status-todo.svg';
import statusReopenIcon from '../../assets/status-reopen.svg';
import statusProgressIcon from '../../assets/status-prog.svg';
import downArrowIcon from '../../assets/down-arrow.svg';
import chatCenteredIcon from '../../assets/ChatCentered.svg';
import plusCircleIcon from '../../assets/PlusCircle.svg';
import fileArrowUpIcon from '../../assets/FileArrowUp.svg';
import subtaskExpandIcon from '../../assets/subtask-expand.svg';
import subtaskCollapseIcon from '../../assets/subtask-collapse.svg';
import TaskTableSkeleton from './TaskTableSkeleton';
import DeleteTaskModal from './DeleteTaskModal';
import TaskDetailsModal from './TaskDetailsModal';
import TaskComposer from './TaskComposer';
import ProjectTaskBoard from './ProjectTaskBoard';
import ProjectGanttChart from './ProjectGanttChart';
import ProjectSummaryModal from './ProjectSummaryModal';
import ProjectDetailsModal from './ProjectDetailsModal';
import TaskReportModal from './TaskReportModal';
import UploadedDocumentsModal from './UploadedDocumentsModal';
import DatePickerInput from '../Common/DatePickerInput';
import { useI18n } from '../../i18n';

// Status and Priority options
const STATUS_OPTIONS = [
  { value: 'todo', label: 'TO DO' },
  { value: 'in_progress', label: 'IN PROGRESS' },
  { value: 'reopen', label: 'REOPEN' },
  { value: 'on_hold', label: 'HOLD' },
  { value: 'done', label: 'COMPLETED' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'LOW' },
  { value: 'medium', label: 'MEDIUM' },
  { value: 'high', label: 'HIGH' },
  { value: 'urgent', label: 'URGENT' },
];

const ProjectTaskList = ({
  onCreateTask,
  onEditTask,
  onEditProject,
  onDeleteProject,
}) => {
  const {
    activeProject,
    tasks,
    isLoading,
    loadingProjectId,
    updateTaskStatus,
    updateTaskPriority,
    deleteTask,
    fetchTasks,
    setFilters,
  } = useProject();
  const { t } = useI18n();
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showProjectSummary, setShowProjectSummary] = useState(false);
  const [showProjectDetails, setShowProjectDetails] = useState(false);
  const [showTaskReport, setShowTaskReport] = useState(false);

  // Subtask composer state
  const [showSubtaskComposer, setShowSubtaskComposer] = useState(false);
  const [subtaskParentTaskId, setSubtaskParentTaskId] = useState(null);

  // Subtasks display state
  const [expandedTaskIds, setExpandedTaskIds] = useState(new Set()); // Track which tasks have expanded subtasks
  const [subtasksCache, setSubtasksCache] = useState({}); // Cache subtasks by parent task ID

  // Upload docs modal state
  const [uploadDocsTask, setUploadDocsTask] = useState(null);
  const [uploadDocsAttachments, setUploadDocsAttachments] = useState([]);
  const [uploadDocsLoading, setUploadDocsLoading] = useState(false);

  // Local state for UI inputs (debounced or immediate)
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activeView, setActiveView] = useState('list'); // list, board, gantt
  const [activeFilter, setActiveFilter] = useState('all'); // all, upcoming, overdue
  const [selectedAssignee, setSelectedAssignee] = useState('all');

  // Infinite scroll state for list view
  const [additionalTasks, setAdditionalTasks] = useState([]);
  const [listPage, setListPage] = useState(1);
  const [listHasMore, setListHasMore] = useState(true);
  const [listLoadingMore, setListLoadingMore] = useState(false);

  // Debounce search query
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Dropdown state for inline editing
  const [statusDropdownTaskId, setStatusDropdownTaskId] = useState(null);
  const [priorityDropdownTaskId, setPriorityDropdownTaskId] = useState(null);
  const [actionDropdownTaskId, setActionDropdownTaskId] = useState(null);
  const statusDropdownRef = useRef(null);
  const priorityDropdownRef = useRef(null);
  const actionDropdownRef = useRef(null);

  // Delete modal state
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Task details modal state
  const [taskToView, setTaskToView] = useState(null);
  const [taskToViewFocusChat, setTaskToViewFocusChat] = useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setStatusDropdownTaskId(null);
      }
      if (
        priorityDropdownRef.current &&
        !priorityDropdownRef.current.contains(event.target)
      ) {
        setPriorityDropdownTaskId(null);
      }
      if (
        actionDropdownRef.current &&
        !actionDropdownRef.current.contains(event.target)
      ) {
        setActionDropdownTaskId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync subtask cache for expanded tasks whenever tasks array changes (e.g. after subtask creation)
  useEffect(() => {
    if (expandedTaskIds.size === 0) return;
    const allTasks = [...tasks, ...additionalTasks];
    setSubtasksCache((prev) => {
      const updated = { ...prev };
      expandedTaskIds.forEach((taskId) => {
        const parentTask = allTasks.find((t) => t.id === taskId);
        if (parentTask?.subtasks) {
          updated[taskId] = parentTask.subtasks;
        }
      });
      return updated;
    });
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch tasks when parameters change
  useEffect(() => {
    if (activeProject?.id) {
      const params = {};

      // === Common Filters ===
      if (debouncedSearchQuery) params.search = debouncedSearchQuery;
      if (startDate) params.start_date = toApiDate(startDate);
      if (endDate) params.end_date = toApiDate(endDate);
      if (selectedAssignee !== 'all') params.assigned_to = selectedAssignee;

      // === View Specific Filters ===
      if (activeView === 'list') {
        params.per_page = 10;
        params.page = 1;
        // Reset scroll state
        setAdditionalTasks([]);
        setListPage(1);
        setListHasMore(false);
        fetchTasks(activeProject.id, params).then((data) => {
          if (data?.pagination) {
            setListHasMore(
              data.pagination.current_page < data.pagination.last_page
            );
            setListPage(data.pagination.current_page);
          } else {
            setListHasMore(false);
          }
        });
      } else if (activeView === 'board') {
        params.per_page = 100;
        fetchTasks(activeProject.id, params);
      } else if (activeView === 'gantt') {
        params.per_page = 100;
        fetchTasks(activeProject.id, params);
      }
    }
  }, [
    activeProject?.id,
    activeView,
    debouncedSearchQuery,
    startDate,
    endDate,
    selectedAssignee,
  ]);

  const handleTaskClick = (taskId) => {
    setSelectedTaskId(taskId);
  };

  const handleClearFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setSearchQuery('');
    setSelectedAssignee('all');
  };

  // Load more tasks for infinite scroll
  const loadMoreListTasks = async () => {
    if (!activeProject?.id || listLoadingMore || !listHasMore) return;
    setListLoadingMore(true);
    try {
      const params = { per_page: 10, page: listPage + 1 };
      if (debouncedSearchQuery) params.search = debouncedSearchQuery;
      if (startDate) params.start_date = toApiDate(startDate);
      if (endDate) params.end_date = toApiDate(endDate);
      if (selectedAssignee !== 'all') params.assigned_to = selectedAssignee;

      const response = await projectService.getTasks(activeProject.id, params);
      const newTasks = response?.data?.tasks || [];
      setAdditionalTasks((prev) => [...prev, ...newTasks]);

      if (response?.data?.pagination) {
        const { current_page, last_page } = response.data.pagination;
        setListHasMore(current_page < last_page);
        setListPage(current_page);
      } else {
        setListHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load more tasks:', error);
    } finally {
      setListLoadingMore(false);
    }
  };

  // Scroll handler for infinite scroll
  const handleListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 150;
    if (isNearBottom && listHasMore && !listLoadingMore) {
      loadMoreListTasks();
    }
  };

  // Handle status change
  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await updateTaskStatus(taskId, newStatus);
      // Update both tasks arrays to keep them in sync
      setAdditionalTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
      );
      setStatusDropdownTaskId(null);
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  // Handle priority change
  const handlePriorityChange = async (taskId, newPriority) => {
    try {
      await updateTaskPriority(taskId, newPriority);
      setAdditionalTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, priority: newPriority } : t))
      );
      setPriorityDropdownTaskId(null);
    } catch (error) {
      console.error('Failed to update priority:', error);
    }
  };

  // Toggle status dropdown
  const toggleStatusDropdown = (e, taskId) => {
    e.stopPropagation();
    setStatusDropdownTaskId(statusDropdownTaskId === taskId ? null : taskId);
    setPriorityDropdownTaskId(null); // Close priority dropdown
  };

  // Toggle priority dropdown
  const togglePriorityDropdown = (e, taskId) => {
    e.stopPropagation();
    setPriorityDropdownTaskId(
      priorityDropdownTaskId === taskId ? null : taskId
    );
    setStatusDropdownTaskId(null); // Close status dropdown
  };

  // Toggle action dropdown
  const toggleActionDropdown = (e, taskId) => {
    e.stopPropagation();
    setActionDropdownTaskId(actionDropdownTaskId === taskId ? null : taskId);
    setStatusDropdownTaskId(null);
    setPriorityDropdownTaskId(null);
  };

  // Handle show task details - open modal
  const handleShowTaskDetails = (e, task) => {
    e.stopPropagation();
    setActionDropdownTaskId(null);
    setTaskToView(task);
  };

  // Close task details modal
  const closeTaskDetailsModal = () => {
    setTaskToView(null);
    setTaskToViewFocusChat(false);
  };

  // Open task details modal with chat focused
  const handleOpenChat = (e, task) => {
    e.stopPropagation();
    setTaskToViewFocusChat(true);
    setTaskToView(task);
  };

  // Open upload docs modal
  const handleOpenUploadDocs = async (e, task) => {
    e.stopPropagation();
    setUploadDocsTask(task);
    setUploadDocsAttachments([]);
    setUploadDocsLoading(true);
    try {
      const response = await projectService.getAttachments(task.id);
      setUploadDocsAttachments(
        response?.attachments || response?.data?.attachments || []
      );
    } catch (error) {
      console.error('Failed to load attachments:', error);
    } finally {
      setUploadDocsLoading(false);
    }
  };

  // Handle edit task from action menu
  const handleEditTaskAction = (e, task) => {
    e.stopPropagation();
    setActionDropdownTaskId(null);
    onEditTask(task);
  };

  // Handle delete task - open modal
  const handleDeleteTask = (e, task) => {
    e.stopPropagation();
    setActionDropdownTaskId(null);
    setTaskToDelete(task);
  };

  // Handle create subtask - open composer with parent task ID
  const handleCreateSubtask = (e, task) => {
    e.stopPropagation();
    setSubtaskParentTaskId(task.id);
    setShowSubtaskComposer(true);
  };

  // Close subtask composer
  const handleCloseSubtaskComposer = () => {
    setShowSubtaskComposer(false);
    setSubtaskParentTaskId(null);
  };

  // Toggle subtask expansion and fetch subtasks
  const handleToggleSubtasks = (e, task) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedTaskIds);
    if (newExpanded.has(task.id)) {
      newExpanded.delete(task.id);
    } else {
      newExpanded.add(task.id);
      // Use subtasks already loaded with the task list — no extra API call
      if (!subtasksCache[task.id]) {
        setSubtasksCache((prev) => ({
          ...prev,
          [task.id]: task.subtasks || [],
        }));
      }
    }
    setExpandedTaskIds(newExpanded);
  };

  // Confirm delete task
  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    setIsDeleting(true);
    try {
      await deleteTask(taskToDelete.id);
      setAdditionalTasks((prev) =>
        prev.filter((t) => t.id !== taskToDelete.id)
      );
      setTaskToDelete(null);
    } catch (error) {
      console.error('Failed to delete task:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    if (!isDeleting) {
      setTaskToDelete(null);
    }
  };

  // Client-side filtering for properties not yet handled by backend (like assignee name vs ID)
  // or simple secondary filtering.
  const getFilteredTasks = (baseTasks) => {
    let filtered = [...baseTasks];

    // Filter out subtasks - only show top-level tasks (parent_task_id is null)
    // Subtasks will be displayed inline under their parent task
    filtered = filtered.filter((task) => !task.parent_task_id);

    // Apply assignee filter (Client-side for now as we have names, not IDs)
    if (selectedAssignee !== 'all') {
      filtered = filtered.filter((task) => {
        const assignee = task.assignee || {};
        const assigneeName =
          assignee.name || assignee.full_name || task.assignee_name;
        return assigneeName === selectedAssignee;
      });
    }

    // Apply active filter (all/upcoming/overdue) if not fully backend integrated yet
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (activeFilter === 'upcoming') {
      filtered = filtered.filter((task) => {
        const taskEndDate = task.end_date || task.due_date;
        if (!taskEndDate) return true;
        const endDate = new Date(taskEndDate);
        return endDate >= today;
      });
    } else if (activeFilter === 'overdue') {
      filtered = filtered.filter((task) => {
        const taskEndDate = task.end_date || task.due_date;
        if (!taskEndDate) return false;
        const endDate = new Date(taskEndDate);
        return endDate < today && task.status !== 'done';
      });
    }

    return filtered;
  };

  const baseTasks =
    activeView === 'list' ? [...tasks, ...additionalTasks] : tasks;
  const filteredTasks = getFilteredTasks(baseTasks);

  const paginatedTasks = filteredTasks;

  // Format a Date object to YYYY-MM-DD string for API params
  const toApiDate = (date) => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const calculateExpectedDays = (startDate, endDate) => {
    if (!startDate || !endDate) return '-';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const calculateDaysUsed = (startDate) => {
    if (!startDate) return '-';
    const start = new Date(startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    if (start > today) return 0; // Not started yet
    const diffTime = today - start;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      todo: 'TO DO',
      in_progress: 'IN PROGRESS',
      done: 'COMPLETED',
      on_hold: 'HOLD',
      reopen: 'REOPEN',
    };
    return statusMap[status] || status?.toUpperCase();
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      todo: statusTodoIcon,
      in_progress: statusProgressIcon,
      done: statusCompleteIcon,
      on_hold: statusHoldIcon,
      reopen: statusReopenIcon,
    };
    const icon = iconMap[status] || statusTodoIcon;
    return <img src={icon} alt="" className="status-icon" />;
  };

  const getStatusClass = (status) => {
    const classMap = {
      todo: 'status-todo',
      in_progress: 'status-in-progress',
      done: 'status-done',
      on_hold: 'status-on-hold',
      reopen: 'status-reopen',
    };
    return classMap[status] || '';
  };

  const getPriorityLabel = (priority) => {
    const priorityMap = {
      low: 'LOW',
      medium: 'MEDIUM',
      high: 'HIGH',
      urgent: 'URGENT',
    };
    return priorityMap[priority] || priority?.toUpperCase() || '-';
  };

  const getPriorityClass = (priority) => {
    const classMap = {
      low: 'priority-low',
      medium: 'priority-medium',
      high: 'priority-high',
      urgent: 'priority-urgent',
    };
    return classMap[priority] || '';
  };

  // Get project members for filter
  const projectMembers = useMemo(() => {
    if (!activeProject?.team?.members) return [];
    return activeProject.team.members;
  }, [activeProject]);

  // Show skeleton loader when switching projects
  if (loadingProjectId || (isLoading && !activeProject)) {
    return (
      <div className="project-task-view">
        <TaskTableSkeleton rowCount={10} />
      </div>
    );
  }

  if (!activeProject) {
    return (
      <div className="project-task-view">
        <div className="task-view-empty">
          <FaSearch size={32} />
          <p>Select a project to view tasks</p>
        </div>
      </div>
    );
  }

  return (
    <div className="project-task-view">
      {/* Header Section */}
      <div className="task-view-header">
        <div className="task-view-title-section">
          <h2 className="task-view-title">
            {activeProject.team?.name || 'Team name'} -{' '}
            <span className="project-name">
              {activeProject.title || activeProject.name}
            </span>
          </h2>
        </div>
        <div className="task-view-actions">
          <div className="settings-container">
            <button
              className="task-header-btn settings-btn"
              onClick={() => setShowSettingsMenu((prev) => !prev)}
            >
              <FaCog size={16} />
            </button>
            {showSettingsMenu && (
              <div className="settings-dropdown">
                <button
                  className="settings-option"
                  onClick={() => {
                    setShowTaskReport(true);
                    setShowSettingsMenu(false);
                  }}
                >
                  <FaList className="option-icon" size={14} />
                  <span>Task Report</span>
                </button>
                <div className="dropdown-divider"></div>

                <button
                  className="settings-option"
                  onClick={() => {
                    setShowProjectSummary(true);
                    setShowSettingsMenu(false);
                  }}
                >
                  <FaChartBar className="option-icon" size={14} />
                  <span>Project Summary</span>
                </button>
                <div className="dropdown-divider"></div>

                <button
                  className="settings-option"
                  onClick={() => {
                    setShowProjectDetails(true);
                    setShowSettingsMenu(false);
                  }}
                >
                  <FaEye className="option-icon" size={14} />
                  <span>Project Details</span>
                </button>
                <div className="dropdown-divider"></div>

                {activeProject.permissions?.can_update && (
                  <>
                    <button
                      className="settings-option edit-option"
                      onClick={() => {
                        onEditProject(activeProject);
                        setShowSettingsMenu(false);
                      }}
                    >
                      <img src={pencilIcon} alt="" className="option-icon" />
                      <span>Edit Project</span>
                    </button>
                    <div className="dropdown-divider"></div>
                  </>
                )}

                {activeProject.permissions?.can_delete && (
                  <>
                    <button
                      className="settings-option delete-option"
                      onClick={() => {
                        onDeleteProject(activeProject);
                        setShowSettingsMenu(false);
                      }}
                    >
                      <img src={trashIcon} alt="" className="option-icon" />
                      <span>Delete Project</span>
                    </button>
                    <div className="dropdown-divider"></div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Buttons Row */}
      <div className="task-view-filter-row">
        <div className="task-filter-buttons">
          <button
            className={`task-filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            All Tasks
          </button>
          <button
            className={`task-filter-tab ${activeFilter === 'upcoming' ? 'active' : ''}`}
            onClick={() => setActiveFilter('upcoming')}
          >
            Upcoming Deadlines
          </button>
          <button
            className={`task-filter-tab ${activeFilter === 'overdue' ? 'active' : ''}`}
            onClick={() => setActiveFilter('overdue')}
          >
            Overdue Tasks
          </button>
        </div>
      </div>

      {/* View Tabs and Create Task Button */}
      <div className="task-view-controls">
        <div className="task-view-tabs">
          <button
            className={`task-view-tab ${activeView === 'list' ? 'active' : ''}`}
            onClick={() => setActiveView('list')}
            title="List View"
          >
            <img src={listIcon} alt="" className="tab-icon" />
            <span>List</span>
          </button>
          <button
            className={`task-view-tab ${activeView === 'board' ? 'active' : ''}`}
            onClick={() => setActiveView('board')}
            title="Board View"
          >
            <img src={boardIcon} alt="" className="tab-icon" />
            <span>Board</span>
          </button>
          <button
            className={`task-view-tab ${activeView === 'gantt' ? 'active' : ''}`}
            onClick={() => setActiveView('gantt')}
            title="Gantt View"
          >
            <img src={ganttIcon} alt="" className="tab-icon" />
            <span>Gantt</span>
          </button>
        </div>
        {activeProject.permissions?.can_create_tasks && (
          <button className="create-task-btn" onClick={onCreateTask}>
            Create Task
          </button>
        )}
      </div>

      {/* Date Filters and Search */}
      <div className="task-date-filters">
        {(activeView === 'list' || activeView === 'gantt') && (
          <div className="date-filter-group">
            <div className="date-input-wrapper">
              <label>Start date</label>
              <DatePickerInput
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                placeholder="Start date"
                popperPlacement="bottom-start"
              />
            </div>
            <div className="date-input-wrapper">
              <label>End date</label>
              <DatePickerInput
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                minDate={startDate || undefined}
                placeholder="End date"
                popperPlacement="bottom-start"
              />
            </div>
            <button className="clear-filter-btn" onClick={handleClearFilter}>
              Clear Filter
            </button>
          </div>
        )}
        <div className="task-date-filters-right">
          <div className="entries-search-group">
            {(activeView === 'list' || activeView === 'board') && (
              <div className="search-input-wrapper">
                <FaSearch size={14} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            )}
          </div>
          {activeView === 'gantt' && (
            <div className="resources-filter-wrapper-combined">
              <select
                value={selectedAssignee}
                onChange={(e) => setSelectedAssignee(e.target.value)}
                className="resources-select-combined"
              >
                <option value="all">Select Resources</option>
                {projectMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
              <div className="search-icon-box">
                <FaSearch size={16} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* View Content */}
      {activeView === 'list' ? (
        <>
          {/* Task Table */}
          <div className="task-table-wrapper">
            <div className="task-table-container" onScroll={handleListScroll}>
              {isLoading ? (
                <div className="task-table-loading">Loading tasks...</div>
              ) : paginatedTasks.length === 0 ? (
                <div className="task-table-empty">
                  <img
                    src={checklistTasksGif}
                    alt="No tasks"
                    className="task-table-empty-gif"
                  />
                  <p className="task-table-empty-title">
                    {t('legacy.no_tasks_created_yet')}
                  </p>
                  <p className="task-table-empty-sub">
                    {t('legacy.create_first_task_hint')}
                  </p>
                </div>
              ) : (
                <table className="task-table">
                  <thead>
                    <tr>
                      <th className="col-task-name">Tasks</th>
                      <th className="col-start-date">Start date</th>
                      <th className="col-end-date">End Date</th>
                      <th className="col-expected-days">Expected Days</th>
                      <th className="col-days-used">Days Used</th>
                      <th className="col-status">Status</th>
                      <th className="col-priority">Priority</th>
                      <th className="col-action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTasks.map((task, index) => (
                      <React.Fragment key={task.id}>
                        <tr
                          className={`task-table-row ${selectedTaskId === task.id ? 'active' : ''} ${index % 2 === 1 ? 'odd' : ''}`}
                          onClick={() => handleTaskClick(task.id)}
                        >
                          <td className="col-task-name">
                            <div className="task-name-cell">
                              {task.subtasks_count > 0 && (
                                <img
                                  src={
                                    expandedTaskIds.has(task.id)
                                      ? subtaskCollapseIcon
                                      : subtaskExpandIcon
                                  }
                                  alt="toggle subtasks"
                                  className="subtask-toggle-arrow"
                                  onClick={(e) => handleToggleSubtasks(e, task)}
                                />
                              )}
                              <span
                                className="task-name-text"
                                title={task.title}
                              >
                                {task.title.length > 7
                                  ? `${task.title.substring(0, 7)}...`
                                  : task.title}
                              </span>
                              <div className="task-name-icons">
                                <img
                                  src={plusCircleIcon}
                                  alt="Create Subtask"
                                  className="task-name-icon task-name-icon-btn"
                                  onClick={(e) => handleCreateSubtask(e, task)}
                                  title="Create Subtask"
                                />
                                <img
                                  src={fileArrowUpIcon}
                                  alt=""
                                  className="task-name-icon task-name-icon-btn"
                                  onClick={(e) => handleOpenUploadDocs(e, task)}
                                />
                                <img
                                  src={chatCenteredIcon}
                                  alt=""
                                  className="task-name-icon task-name-icon-btn"
                                  onClick={(e) => handleOpenChat(e, task)}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="col-start-date">
                            {formatDate(task.start_date)}
                          </td>
                          <td className="col-end-date">
                            {formatDate(task.end_date || task.due_date)}
                          </td>
                          <td className="col-expected-days">
                            {task.expected_days ||
                              calculateExpectedDays(
                                task.start_date,
                                task.end_date || task.due_date
                              )}
                          </td>
                          <td className="col-days-used">
                            {calculateDaysUsed(task.start_date)}
                          </td>
                          <td className="col-status">
                            <div
                              className="inline-dropdown-container"
                              ref={
                                statusDropdownTaskId === task.id
                                  ? statusDropdownRef
                                  : null
                              }
                            >
                              <span
                                className={`status-badge ${getStatusClass(task.status)}`}
                                onClick={(e) =>
                                  toggleStatusDropdown(e, task.id)
                                }
                              >
                                {getStatusIcon(task.status)}
                                {getStatusLabel(task.status)}
                                <img
                                  src={downArrowIcon}
                                  alt=""
                                  className="dropdown-arrow"
                                />
                              </span>
                              {statusDropdownTaskId === task.id && (
                                <div className="inline-dropdown-menu status-dropdown-menu">
                                  {STATUS_OPTIONS.map((option) => (
                                    <div
                                      key={option.value}
                                      className={`inline-dropdown-item ${task.status === option.value ? 'active' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStatusChange(
                                          task.id,
                                          option.value
                                        );
                                      }}
                                    >
                                      <span
                                        className={`status-badge ${getStatusClass(option.value)}`}
                                      >
                                        {getStatusIcon(option.value)}
                                        {option.label}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="col-priority">
                            <div
                              className="inline-dropdown-container"
                              ref={
                                priorityDropdownTaskId === task.id
                                  ? priorityDropdownRef
                                  : null
                              }
                            >
                              <span
                                className={`priority-badge ${getPriorityClass(task.priority)}`}
                                onClick={(e) =>
                                  togglePriorityDropdown(e, task.id)
                                }
                              >
                                {getPriorityLabel(task.priority)}
                                <img
                                  src={downArrowIcon}
                                  alt=""
                                  className="dropdown-arrow priority-arrow"
                                />
                              </span>
                              {priorityDropdownTaskId === task.id && (
                                <div className="inline-dropdown-menu priority-dropdown-menu">
                                  {PRIORITY_OPTIONS.map((option) => (
                                    <div
                                      key={option.value}
                                      className={`inline-dropdown-item ${task.priority === option.value ? 'active' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePriorityChange(
                                          task.id,
                                          option.value
                                        );
                                      }}
                                    >
                                      <span
                                        className={`priority-badge ${getPriorityClass(option.value)}`}
                                      >
                                        {option.label}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="col-action">
                            <div
                              className="action-dropdown-container"
                              ref={
                                actionDropdownTaskId === task.id
                                  ? actionDropdownRef
                                  : null
                              }
                            >
                              <button
                                className="action-menu-btn"
                                onClick={(e) =>
                                  toggleActionDropdown(e, task.id)
                                }
                                title="Actions"
                              >
                                <img
                                  src={dotsIcon}
                                  alt="Actions"
                                  className="action-dots-icon"
                                />
                              </button>
                              {actionDropdownTaskId === task.id && (
                                <div className="action-dropdown-menu">
                                  <div
                                    className="action-dropdown-item"
                                    onClick={(e) =>
                                      handleShowTaskDetails(e, task)
                                    }
                                  >
                                    <FaEye className="action-dropdown-icon" />
                                    <span>Show Task Details</span>
                                  </div>
                                  {task.permissions?.can_update && (
                                    <div
                                      className="action-dropdown-item"
                                      onClick={(e) =>
                                        handleEditTaskAction(e, task)
                                      }
                                    >
                                      <img
                                        src={pencilIcon}
                                        alt=""
                                        className="action-dropdown-icon"
                                      />
                                      <span>Edit Task</span>
                                    </div>
                                  )}
                                  {task.permissions?.can_delete && (
                                    <div
                                      className="action-dropdown-item delete-item"
                                      onClick={(e) => handleDeleteTask(e, task)}
                                    >
                                      <img
                                        src={trashIcon}
                                        alt=""
                                        className="action-dropdown-icon"
                                      />
                                      <span>Delete Task</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Subtasks */}
                        {expandedTaskIds.has(task.id) &&
                          subtasksCache[task.id] &&
                          subtasksCache[task.id].map((subtask, subIndex) => (
                            <tr
                              key={`subtask-${subtask.id}`}
                              className={`subtask-data-row ${subIndex % 2 === 1 ? 'odd' : ''}`}
                            >
                              <td className="col-task-name">
                                <div className="subtask-name-cell">
                                  <span className="subtask-indicator">└─</span>
                                  <span
                                    className="task-name-text"
                                    title={subtask.title}
                                  >
                                    {subtask.title.length > 7
                                      ? `${subtask.title.substring(0, 7)}...`
                                      : subtask.title}
                                  </span>
                                  <div className="task-name-icons">
                                    <img
                                      src={fileArrowUpIcon}
                                      alt=""
                                      className="task-name-icon task-name-icon-btn"
                                      onClick={(e) =>
                                        handleOpenUploadDocs(e, subtask)
                                      }
                                    />
                                    <img
                                      src={chatCenteredIcon}
                                      alt=""
                                      className="task-name-icon task-name-icon-btn"
                                      onClick={(e) =>
                                        handleOpenChat(e, subtask)
                                      }
                                    />
                                  </div>
                                </div>
                              </td>
                              <td className="col-start-date">
                                {formatDate(subtask.start_date)}
                              </td>
                              <td className="col-end-date">
                                {formatDate(
                                  subtask.end_date || subtask.due_date
                                )}
                              </td>
                              <td className="col-expected-days">
                                {subtask.expected_days ||
                                  calculateExpectedDays(
                                    subtask.start_date,
                                    subtask.end_date || subtask.due_date
                                  )}
                              </td>
                              <td className="col-days-used">
                                {calculateDaysUsed(subtask.start_date)}
                              </td>
                              <td className="col-status">
                                <span
                                  className={`status-badge ${getStatusClass(subtask.status)}`}
                                >
                                  {getStatusIcon(subtask.status)}
                                  {getStatusLabel(subtask.status)}
                                </span>
                              </td>
                              <td className="col-priority">
                                <span
                                  className={`priority-badge ${getPriorityClass(subtask.priority)}`}
                                >
                                  {getPriorityLabel(subtask.priority)}
                                </span>
                              </td>
                              <td className="col-action">
                                <div
                                  className="action-dropdown-container"
                                  ref={
                                    actionDropdownTaskId === `sub-${subtask.id}`
                                      ? actionDropdownRef
                                      : null
                                  }
                                >
                                  <button
                                    className="action-menu-btn"
                                    onClick={(e) =>
                                      toggleActionDropdown(
                                        e,
                                        `sub-${subtask.id}`
                                      )
                                    }
                                    title="Actions"
                                  >
                                    <img
                                      src={dotsIcon}
                                      alt="Actions"
                                      className="action-dots-icon"
                                    />
                                  </button>
                                  {actionDropdownTaskId ===
                                    `sub-${subtask.id}` && (
                                    <div className="action-dropdown-menu">
                                      <div
                                        className="action-dropdown-item"
                                        onClick={(e) =>
                                          handleShowTaskDetails(e, subtask)
                                        }
                                      >
                                        <FaEye className="action-dropdown-icon" />
                                        <span>Show Task Details</span>
                                      </div>
                                      {subtask.permissions?.can_update && (
                                        <div
                                          className="action-dropdown-item"
                                          onClick={(e) =>
                                            handleEditTaskAction(e, subtask)
                                          }
                                        >
                                          <img
                                            src={pencilIcon}
                                            alt=""
                                            className="action-dropdown-icon"
                                          />
                                          <span>Edit Task</span>
                                        </div>
                                      )}
                                      {subtask.permissions?.can_delete && (
                                        <div
                                          className="action-dropdown-item delete-item"
                                          onClick={(e) =>
                                            handleDeleteTask(e, subtask)
                                          }
                                        >
                                          <img
                                            src={trashIcon}
                                            alt=""
                                            className="action-dropdown-icon"
                                          />
                                          <span>Delete Task</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
              {listLoadingMore && (
                <div className="scroll-loading-indicator">
                  Loading more tasks...
                </div>
              )}
            </div>
          </div>
        </>
      ) : activeView === 'board' ? (
        <ProjectTaskBoard
          tasks={filteredTasks}
          onStatusChange={handleStatusChange}
          onTaskClick={null}
          onShowTaskDetails={handleShowTaskDetails}
          onEditTask={handleEditTaskAction}
          onDeleteTask={handleDeleteTask}
          onCreateTask={(status) => onCreateTask(status)}
          onCreateSubtask={handleCreateSubtask}
          onOpenChat={handleOpenChat}
          onOpenUploadDocs={handleOpenUploadDocs}
        />
      ) : (
        <div className="task-table-wrapper">
          <ProjectGanttChart tasks={filteredTasks} onTaskClick={null} />
        </div>
      )}

      {/* Delete Task Modal */}
      <DeleteTaskModal
        isOpen={!!taskToDelete}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteTask}
        task={taskToDelete}
        loading={isDeleting}
      />

      {/* Task Details Modal */}
      <TaskDetailsModal
        isOpen={!!taskToView}
        onClose={closeTaskDetailsModal}
        task={taskToView}
        focusChat={taskToViewFocusChat}
      />

      {/* Project Details Modal */}
      <ProjectDetailsModal
        isOpen={showProjectDetails}
        onClose={() => setShowProjectDetails(false)}
        project={activeProject}
      />

      {/* Project Summary Modal */}
      <ProjectSummaryModal
        isOpen={showProjectSummary}
        onClose={() => setShowProjectSummary(false)}
        project={activeProject}
      />

      <TaskReportModal
        isOpen={showTaskReport}
        onClose={() => setShowTaskReport(false)}
        project={activeProject}
      />

      <UploadedDocumentsModal
        isOpen={!!uploadDocsTask}
        onClose={() => {
          setUploadDocsTask(null);
          setUploadDocsAttachments([]);
          setUploadDocsLoading(false);
        }}
        task={uploadDocsTask}
        loading={uploadDocsLoading}
        attachments={uploadDocsAttachments}
        onUpload={(newAttachment) => {
          setUploadDocsAttachments((prev) => [newAttachment, ...prev]);
        }}
        onView={(attachment) => {
          const url = attachment.upload?.file_url;
          if (url) window.open(url, '_blank');
        }}
        onDownload={async (attachment) => {
          try {
            const response = await api.get(
              `/v1/tasks/${uploadDocsTask.id}/attachments/${attachment.id}/download`,
              { responseType: 'blob' }
            );
            const blob = new Blob([response]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download =
              attachment.upload?.original_filename ||
              attachment.file_name ||
              'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (err) {
            console.error('Download failed:', err);
          }
        }}
        onDownloadAll={async () => {
          try {
            const response = await api.get(
              `/v1/tasks/${uploadDocsTask.id}/attachments/download-all`,
              { responseType: 'blob' }
            );
            const blob = new Blob([response]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${uploadDocsTask.title || 'task'}-attachments.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (err) {
            console.error('Download all failed:', err);
          }
        }}
      />

      {/* Subtask Composer Modal */}
      <TaskComposer
        isOpen={showSubtaskComposer}
        onClose={handleCloseSubtaskComposer}
        projectId={activeProject?.id}
        parentTaskId={subtaskParentTaskId}
      />
    </div>
  );
};

export default ProjectTaskList;
